import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_KEYS, PORTAL_META, STAFF_VIEW_KEY, setStaffView } from '../contexts/MarketContext';

// A topbar dropdown that lets a user move between the portals they can access —
// the three branded/localized shells (/staff, /es, /pad) plus a combined
// "All Tours" roll-up (English /staff shell) when they have 2+ portals. Renders
// nothing when there's only one place to go. Styled for a dark topbar, so it
// drops into every shell's header unchanged.

function storedStaffView() {
  try { return localStorage.getItem(STAFF_VIEW_KEY) === 'all' ? 'all' : 'uk_to_spain'; } catch { return 'uk_to_spain'; }
}

function currentSelection(pathname) {
  if (pathname.startsWith('/es')) return 'spain_to_uk';
  if (pathname.startsWith('/pad')) return 'padelante';
  return storedStaffView(); // on /staff: 'uk_to_spain' or 'all'
}

export default function PortalSwitcher() {
  const { userPortals, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const portals = isAdmin ? PORTAL_KEYS : (userPortals || []);
  const options = [
    ...PORTAL_KEYS.filter((k) => portals.includes(k)).map((k) => ({ key: k, label: PORTAL_META[k].label })),
    ...(portals.length >= 2 ? [{ key: 'all', label: 'All Tours' }] : []),
  ];

  // Nothing meaningful to switch between.
  if (options.length < 2) return null;

  const current = currentSelection(pathname);
  const currentLabel = options.find((o) => o.key === current)?.label || options[0].label;

  function choose(key) {
    setOpen(false);
    const onStaff = pathname.startsWith('/staff');
    if (key === 'all') {
      // Combined roll-up lives in /staff — keep the current section when already there.
      setStaffView('all');
      navigate(onStaff ? pathname : '/staff');
    } else if (key === 'uk_to_spain') {
      setStaffView('uk_to_spain');
      navigate(onStaff ? pathname : '/staff');
    } else {
      // Switching to the Spain / Padelante shell.
      navigate(PORTAL_META[key].base);
    }
  }

  return (
    <div className="relative block min-w-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch portal"
        aria-expanded={open}
        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-[9px] pl-[11px] pr-[9px] h-9 transition min-w-0 max-w-[46vw] md:max-w-none"
      >
        <svg className="w-4 h-4 text-white/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.9}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H8m0 0l4 4m-4-4l4-4" />
        </svg>
        {/* Label is hidden on the cramped mobile topbar — the ⇄ + chevron read as a
            tappable menu, and the dropdown reveals every portal name on tap. */}
        <span className="hidden sm:block text-[12.5px] font-bold text-white whitespace-nowrap truncate">{currentLabel}</span>
        <svg className={`w-[13px] h-[13px] flex-shrink-0 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-[12px] shadow-lg overflow-hidden z-50">
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => choose(opt.key)}
              className={`w-full flex items-center justify-between gap-2 text-left px-4 py-2.5 text-[13px] font-medium transition ${
                current === opt.key ? 'bg-blue-light text-pb' : 'text-[#374151] hover:bg-surface'
              }`}
            >
              {opt.label}
              {current === opt.key && (
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
